import { useState, useEffect } from 'react';
import { adminApi, type DepositChannel } from '@/lib/admin';
import { useToast } from '@/components/base/Toast';
import { SectionCard, EmptyState, LoadingState, ErrorState, ConfirmDialog } from '@/pages/admin/components/UI';

const channelTypes = ['银行卡', '微信', 'USDT', '人工审核入金'];

const typeMeta: Record<string, { icon: string; color: string }> = {
  银行卡: { icon: 'ri-bank-card-line', color: 'bg-gold-100 text-gold-700' },
  微信: { icon: 'ri-wechat-line', color: 'bg-green-100 text-green-600' },
  USDT: { icon: 'ri-coin-line', color: 'bg-amber-100 text-amber-600' },
  人工审核入金: { icon: 'ri-customer-service-line', color: 'bg-brown-100 text-brown-700' },
};

const typeTabs = [
  { key: 'all', label: '全部' },
  ...channelTypes.map((t) => ({ key: t, label: t })),
];

interface FormState {
  channel_type: string;
  bank_name: string;
  account_name: string;
  account_no: string;
  chain_type: string;
  remark: string;
  enabled: boolean;
  exchange_rate: string;
}

const emptyForm: FormState = {
  channel_type: '银行卡',
  bank_name: '',
  account_name: '',
  account_no: '',
  chain_type: 'TRC20',
  remark: '',
  enabled: true,
  exchange_rate: '',
};

export default function AdminChannels() {
  const { showToast } = useToast();
  const [channels, setChannels] = useState<DepositChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<DepositChannel | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<DepositChannel | null>(null);
  const [busy, setBusy] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await adminApi.listChannels();
      setChannels(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (c: DepositChannel) => {
    setEditing(c);
    setForm({
      channel_type: c.channel_type,
      bank_name: c.bank_name || '',
      account_name: c.account_name || '',
      account_no: c.account_no || '',
      chain_type: c.chain_type || 'TRC20',
      remark: c.remark || '',
      enabled: c.enabled,
      exchange_rate: c.exchange_rate?.toString() ?? '',
    });
    setFormOpen(true);
  };

  const saveForm = async () => {
    if (!form.account_name.trim() || !form.account_no.trim()) {
      showToast('请填写收款人名称和收款账号', 'error');
      return;
    }
    setBusy(true);
    try {
      const payload: any = {
        ...form,
        exchange_rate: form.exchange_rate && form.exchange_rate.trim() !== '' ? Number(form.exchange_rate) : null,
      };
      if (editing) {
        await adminApi.updateChannel({ id: editing.id, ...payload });
        showToast('通道已更新', 'success');
      } else {
        await adminApi.createChannel(payload);
        showToast('通道已添加', 'success');
      }
      setFormOpen(false);
      await fetchData();
    } catch (e) {
      showToast(e instanceof Error ? e.message : '保存失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  const toggleEnabled = async (c: DepositChannel) => {
    try {
      await adminApi.updateChannel({ id: c.id, enabled: !c.enabled });
      showToast(!c.enabled ? '已启用' : '已停用', 'success');
      await fetchData();
    } catch (e) {
      showToast(e instanceof Error ? e.message : '操作失败', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await adminApi.deleteChannel(deleteTarget.id);
      showToast('通道已删除', 'success');
      setDeleteTarget(null);
      await fetchData();
    } catch (e) {
      showToast(e instanceof Error ? e.message : '删除失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  const filtered = tab === 'all' ? channels : channels.filter((c) => c.channel_type === tab);
  const enabledCount = channels.filter((c) => c.enabled).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-brown-900 text-lg font-bold">收款通道</h2>
          <p className="text-brown-400 text-xs mt-0.5">
            共 {channels.length} 个通道 · 启用 {enabledCount} 个，客户入金时将展示已启用的通道
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gold-500 text-brown-950 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-gold-600 transition-colors self-start"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-base"></i>
          </span>
          新增通道
        </button>
      </div>

      <div className="inline-flex bg-white rounded-lg border border-brown-100 p-1 flex-wrap">
        {typeTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
              tab === t.key ? 'bg-gold-500 text-brown-950' : 'text-brown-500 hover:text-brown-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <SectionCard>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState onRetry={fetchData} />
        ) : filtered.length === 0 ? (
          <EmptyState title="暂无收款通道" desc="点右上角「新增通道」添加银行卡 / 微信 / USDT 等收款方式" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-brown-400 text-xs border-b border-brown-100">
                  <th className="px-4 md:px-5 py-3 font-medium">类型</th>
                  <th className="px-4 py-3 font-medium">收款信息</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">备注</th>
                  <th className="px-4 py-3 font-medium">状态</th>
                  <th className="px-4 py-3 font-medium text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const meta = typeMeta[c.channel_type] || typeMeta['银行卡'];
                  return (
                    <tr key={c.id} className="border-b border-brown-50 last:border-0 hover:bg-brown-50/40 transition-colors">
                      <td className="px-4 md:px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta.color}`}>
                            <span className="w-4 h-4 flex items-center justify-center">
                              <i className={`${meta.icon} text-base`}></i>
                            </span>
                          </span>
                          <span className="text-brown-900 font-medium">{c.channel_type}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <p className="text-brown-900 font-medium">
                          {c.account_name}
                          {c.bank_name && <span className="text-brown-400 text-xs ml-1">({c.bank_name})</span>}
                        </p>
                        <p className="text-brown-500 text-xs mt-0.5 font-mono">{c.account_no}</p>
                        {c.chain_type && c.channel_type === 'USDT' && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-brown-100 text-brown-500 mt-1 inline-block">
                            {c.chain_type}
                          </span>
                        )}
                        {c.channel_type === 'USDT' && c.exchange_rate && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 mt-1 inline-block ml-1">
                            汇率 ¥{Number(c.exchange_rate).toFixed(2)}/USDT
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 hidden md:table-cell text-brown-500">
                        {c.remark || <span className="text-brown-300">—</span>}
                      </td>
                      <td className="px-4 py-3.5">
                        <button
                          onClick={() => toggleEnabled(c)}
                          className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${
                            c.enabled ? 'bg-green-500' : 'bg-brown-200'
                          }`}
                          title={c.enabled ? '点击停用' : '点击启用'}
                        >
                          <span
                            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                              c.enabled ? 'left-[18px]' : 'left-0.5'
                            }`}
                          ></span>
                        </button>
                      </td>
                      <td className="px-4 py-3.5 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => openEdit(c)}
                            className="px-3 py-1.5 rounded-md border border-brown-200 text-brown-600 text-xs cursor-pointer whitespace-nowrap hover:bg-brown-50 transition-colors"
                          >
                            编辑
                          </button>
                          <button
                            onClick={() => setDeleteTarget(c)}
                            className="px-3 py-1.5 rounded-md border border-red-100 text-red-500 text-xs cursor-pointer whitespace-nowrap hover:bg-red-50 transition-colors"
                          >
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Add / Edit Modal */}
      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setFormOpen(false)}></div>
          <div className="relative bg-white rounded-xl w-full max-w-lg p-5 animate-fade-up max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-brown-900 text-base font-bold">{editing ? '编辑收款通道' : '新增收款通道'}</h3>
              <button
                onClick={() => setFormOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-brown-400 hover:text-brown-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-brown-600 text-sm mb-1.5">通道类型</label>
                <div className="flex gap-2 flex-wrap">
                  {channelTypes.map((t) => (
                    <button
                      key={t}
                      onClick={() => setForm({ ...form, channel_type: t, chain_type: t === 'USDT' ? form.chain_type : '' })}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                        form.channel_type === t
                          ? 'bg-gold-500 text-brown-950'
                          : 'bg-brown-50 text-brown-500 border border-brown-200'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-brown-600 text-sm mb-1.5">收款人名称 *</label>
                  <input
                    type="text"
                    value={form.account_name}
                    onChange={(e) => setForm({ ...form, account_name: e.target.value })}
                    placeholder={form.channel_type === '银行卡' ? '开户人姓名' : '收款人名称'}
                    className="w-full text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-lg px-3 py-2 bg-white outline-none focus:border-gold-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-brown-600 text-sm mb-1.5">收款账号 *</label>
                  <input
                    type="text"
                    value={form.account_no}
                    onChange={(e) => setForm({ ...form, account_no: e.target.value })}
                    placeholder={
                      form.channel_type === '银行卡' ? '银行卡号' : form.channel_type === '微信' ? '微信号' : '钱包地址'
                    }
                    className="w-full text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-lg px-3 py-2 bg-white outline-none focus:border-gold-500 transition-colors"
                  />
                </div>
                {form.channel_type === '银行卡' && (
                  <div>
                    <label className="block text-brown-600 text-sm mb-1.5">收款银行</label>
                    <input
                      type="text"
                      value={form.bank_name}
                      onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                      placeholder="如：招商银行"
                      className="w-full text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-lg px-3 py-2 bg-white outline-none focus:border-gold-500 transition-colors"
                    />
                  </div>
                )}
                {form.channel_type === 'USDT' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-brown-600 text-sm mb-1.5">链类型</label>
                      <select
                        value={form.chain_type}
                        onChange={(e) => setForm({ ...form, chain_type: e.target.value })}
                        className="w-full text-sm text-brown-900 border border-brown-200 rounded-lg px-3 py-2 bg-white outline-none focus:border-gold-500 transition-colors"
                      >
                        <option value="TRC20">TRC20</option>
                        <option value="ERC20">ERC20</option>
                        <option value="BEP20">BEP20</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-brown-600 text-sm mb-1.5">USDT 汇率 (¥/USDT)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={form.exchange_rate}
                        onChange={(e) => setForm({ ...form, exchange_rate: e.target.value })}
                        placeholder="如：7.25"
                        className="w-full text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-lg px-3 py-2 bg-white outline-none focus:border-gold-500 transition-colors"
                      />
                      <p className="text-brown-400 text-xs mt-0.5">1 USDT 等于多少人民币</p>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-brown-600 text-sm mb-1.5">备注</label>
                <input
                  type="text"
                  value={form.remark}
                  onChange={(e) => setForm({ ...form, remark: e.target.value })}
                  placeholder="客户转账时的提示说明（选填）"
                  maxLength={100}
                  className="w-full text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-lg px-3 py-2 bg-white outline-none focus:border-gold-500 transition-colors"
                />
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setForm({ ...form, enabled: !form.enabled })}
                  className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer ${
                    form.enabled ? 'bg-green-500' : 'bg-brown-200'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                      form.enabled ? 'left-[18px]' : 'left-0.5'
                    }`}
                  ></span>
                </button>
                <span className="text-brown-600 text-sm">{form.enabled ? '启用（客户入金可见）' : '停用'}</span>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setFormOpen(false)}
                className="flex-1 py-2.5 rounded-lg border border-brown-200 text-brown-600 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-brown-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={saveForm}
                disabled={busy}
                className="flex-1 py-2.5 rounded-lg bg-gold-500 text-brown-950 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-gold-600 transition-colors disabled:opacity-60"
              >
                {busy ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="删除该收款通道？"
        desc="删除后客户入金时将不再展示此通道。"
        confirmText="删除"
        danger
        loading={busy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}